import { consume, createContext, provide } from "@lit/context";
import { Task, TaskStatus } from "@lit/task";
import { html, LitElement, nothing, type TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { runAuction } from "./auction";
import { TopsortConfigurationError } from "./errors";
import { BannerComponent } from "./mixin";
import { applyTemplate } from "./template";
import type { Banner, BannerContext, HlsConstructor, LinkContext } from "./types";

/* Set up global environment for TS_BANNERS */

declare global {
  interface Window {
    TS_BANNERS: {
      getLink(banner: Banner, context: LinkContext): string;
      resolveLink(href: string, banner: Banner, context: LinkContext): string;
      getLoadingElement(): HTMLElement;
      getErrorElement(error: unknown): HTMLElement;
      getNoWinnersElement(): HTMLElement;
      getBannerElement(banner: Banner): HTMLElement;
    };
    TS: {
      readonly token: string;
      readonly url?: string;
      getUserId?: () => string;
      readonly gatedImpressions?: boolean;
    };
  }
}

window.TS_BANNERS = window.TS_BANNERS || {};

/**
 * The winning bid goes into `data-ts-resolved-bid` as soon as the auction
 * resolves. analytics.js decides when that counts as an impression, and only
 * reports one when the element is both in the viewport and actually painted.
 *
 * An older analytics.js has no such check, so a banner preloaded into a hidden
 * container (a mega-menu revealed on hover, say) would be counted on page load.
 *
 * @internal exposed for testing only
 */
export function warnIfImpressionsUngated(): void {
  if (window.TS?.gatedImpressions) {
    return;
  }
  console.warn(
    "[banners.js] This version requires @topsort/analytics.js with visibility-gated impressions. " +
      "The loaded analytics.js is older or missing, so banners inside hidden containers may " +
      "record an impression before the shopper sees them. Upgrade @topsort/analytics.js.",
  );
}

// Deferred to `load`, since script order varies and analytics.js is often loaded
// after this one.
if (document.readyState === "complete") {
  warnIfImpressionsUngated();
} else {
  window.addEventListener("load", warnIfImpressionsUngated, { once: true });
}

/**
 * Content field carrying a third-party impression tracker (e.g. a Google
 * Campaign Manager pixel) set on the campaign. It needs no `data-ts-field`
 * binding, and a standard-mode banner carrying it still renders.
 */
const IMPRESSION_PIXEL_FIELD = "impressionPixelUrl";

// resolvedBidId -> tracker URL, for winners whose impression has not fired yet.
const pendingPixels = new Map<string, string>();

/**
 * Standard mode only: true when the winner carries content meant for a
 * predefined template, which standard rendering cannot show. Predefined mode
 * applies any content map, because `applyTemplate` is also what writes
 * `data-ts-resolved-bid` — skipping it would lose the impression.
 */
function hasTemplateContent(banner: Banner): boolean {
  const content = banner.asset?.[0]?.content;
  return !!content && Object.keys(content).some((key) => key !== IMPRESSION_PIXEL_FIELD);
}

function registerImpressionPixels(banners: Banner[]): void {
  for (const banner of banners) {
    const url = banner.asset?.[0]?.content?.[IMPRESSION_PIXEL_FIELD];
    // Fallbacks carry no bid, so analytics.js never reports an impression for them.
    if (url && !banner.isFallback) {
      pendingPixels.set(banner.resolvedBidId, url);
    }
  }
}

/**
 * Fires the tracker when analytics.js reports the Topsort impression, so both
 * sides count the same viewable impression (50% in view, painted, for 1s).
 * Without gating, analytics.js would report hidden or off-screen banners too.
 */
function fireImpressionPixel(event: Event): void {
  const detail = (event as CustomEvent<{ type?: string; bid?: string }>).detail;
  if (detail?.type !== "Impression" || !detail.bid || !window.TS?.gatedImpressions) {
    return;
  }
  const url = pendingPixels.get(detail.bid);
  if (!url) {
    return;
  }
  pendingPixels.delete(detail.bid);
  const cacheBuster = `${Date.now()}${Math.floor(Math.random() * 1e6)}`;
  // Never attached to the DOM, so it cannot affect the ad's layout.
  new Image().src = url.replace(/\[timestamp\]|%%CACHEBUSTER%%/gi, cacheBuster);
}

document.addEventListener("topsort", fireImpressionPixel);

function logError(error: unknown) {
  if (import.meta.env.DEV) {
    console.error(error);
  }
}

// The following methods are used to customize the appearance of the banner component.
function getLink(banner: Banner, context: LinkContext): string {
  if (window.TS_BANNERS.getLink) {
    return window.TS_BANNERS.getLink(banner, context);
  }
  if (banner.type === "url") {
    return banner.id;
  }
  return `${banner.type}/${banner.id}`;
}

/**
 * Predefined mode only. Takes the URL a template binding is about to write and
 * returns the one to use, so an incomplete destination can be completed from
 * the context it is rendering in.
 *
 * Deliberately not part of `getLink`: that hook *generates* a link from a
 * winner and returns one link per banner, while this one *transforms* a URL the
 * auction already supplied and runs per `href` binding — a template may have
 * several. Merging them would also apply a merchant's existing standard-mode
 * `getLink` to their campaign URLs here, silently replacing them.
 *
 * Undefined by default, so a template's URL is written exactly as the auction
 * returned it.
 */
function resolveLink(href: string, banner: Banner, context: LinkContext): string {
  if (window.TS_BANNERS.resolveLink) {
    return window.TS_BANNERS.resolveLink(href, banner, context);
  }
  return href;
}

function getLoadingElement(): TemplateResult {
  if (window.TS_BANNERS.getLoadingElement) {
    const element = window.TS_BANNERS.getLoadingElement();
    return html`${element}`;
  }
  // By default, hide the component while loading
  return html``;
}

function getErrorElement(error: unknown): TemplateResult {
  if (window.TS_BANNERS.getErrorElement) {
    const element = window.TS_BANNERS.getErrorElement(error);
    return html`${element}`;
  }
  // By default, hide the component if there is an error
  return html``;
}

function getNoWinnersElement(): TemplateResult {
  if (window.TS_BANNERS.getNoWinnersElement) {
    const element = window.TS_BANNERS.getNoWinnersElement();
    return html`${element}`;
  }
  // By default, hide the component if there are no winners
  return html``;
}

function getBannerElement(
  banner: Banner,
  width: number,
  height: number,
  newTab: boolean,
  context: LinkContext,
): TemplateResult {
  if (window.TS_BANNERS.getBannerElement) {
    const element = window.TS_BANNERS.getBannerElement(banner);
    return html`${element}`;
  }

  if (!banner.asset?.[0]?.url) {
    logError(new Error("Invalid banner asset: missing URL"));
    return html``;
  }
  const src = banner.asset[0].url;

  // width/height default to 0 when the attribute is omitted; treat 0 as "not set"
  // and let the banner fluidly fill its container at the asset's native aspect ratio.
  const widthStyle = width > 0 ? `${width}px` : "100%";
  const heightStyle = height > 0 ? `${height}px` : "auto";

  const isVideo = (() => {
    try {
      const url = new URL(src);
      const parts = url.pathname.split("/");
      const manifestIndex = parts.indexOf("manifest");
      if (manifestIndex >= 0) {
        const nextSegment = parts[manifestIndex + 1];
        return nextSegment?.startsWith("video") ?? false;
      }
      return false;
    } catch {
      return false;
    }
  })();

  const media = isVideo
    ? html`
        <hls-video
          src="${src}"
          width="${widthStyle}"
          height="${heightStyle}"
        ></hls-video>
      `
    : html`
        <img
          src="${src}"
          alt="Topsort banner"
          style="width:${widthStyle}; height:${heightStyle}; object-fit:cover;"
        />
      `;

  const href = getLink(banner, context);
  const wrappedMedia = newTab
    ? html`<a href="${href}" target="_blank">${media}</a>`
    : html`<a href="${href}">${media}</a>`;
  return html`
    <div
      data-ts-clickable
      data-ts-resolved-bid=${ifDefined(banner.isFallback ? undefined : banner.resolvedBidId)}
      class="ts-banner"
    >
      ${wrappedMedia}
    </div>
  `;
}

const bannerContext = createContext<BannerContext>(Symbol("banner-context"));
const bannerContextHasChanged = (newVal: BannerContext, oldVal?: BannerContext) => {
  if (!oldVal && newVal) {
    return true;
  }
  if (newVal == null || oldVal == null) {
    return false;
  }
  return (
    newVal.width !== oldVal.width ||
    newVal.height !== oldVal.height ||
    newVal.newTab !== oldVal.newTab ||
    newVal.language !== oldVal.language ||
    newVal.location !== oldVal.location ||
    !!newVal.error !== !!oldVal.error ||
    newVal.banners?.length !== oldVal.banners?.length
  );
};

/**
 * A banner web component that runs an auction and renders the winning banner.
 */
@customElement("topsort-banner")
export class TopsortBanner extends BannerComponent(LitElement) {
  private task = new Task(this, {
    task: ([slots], options) =>
      runAuction(this.buildAuction(slots), { ...options, logError })
        .then((winners) => {
          options.signal.throwIfAborted();
          registerImpressionPixels(winners);
          if (this.isContext) {
            this.context = { ...this.context, banners: winners };
          }
          return winners;
        })
        .catch((error) => {
          options.signal.throwIfAborted();
          if (this.isContext) {
            this.context = { ...this.context, error };
          }
          throw error;
        }),
    args: () => [this.slots?.length || 1],
  });

  @provide({ context: bannerContext })
  @property({ attribute: false, hasChanged: bannerContextHasChanged })
  private context: BannerContext = {
    width: this.width,
    height: this.height,
    newTab: this.newTab,
    language: this.language,
    location: this.location,
    slotId: this.slotId,
  };

  @property({ type: Boolean, attribute: "context" })
  readonly isContext: boolean = false;

  @property({ type: Boolean })
  readonly predefined: boolean = false;

  @property({ attribute: "language", type: String })
  readonly language?: string;

  private _prevTaskStatus: TaskStatus = TaskStatus.INITIAL;

  @property({ attribute: false, state: true })
  private slots?: NodeListOf<Element>;

  protected render() {
    if (!window.TS.token || !this.slotId) {
      return getErrorElement(new TopsortConfigurationError(window.TS.token, this.slotId));
    }
    if (this.isContext) {
      return html``;
    }
    if (this.predefined) {
      return nothing;
    }
    return this.task.render({
      pending: () => getLoadingElement(),
      complete: (banners) => {
        if (!banners.length) {
          return getNoWinnersElement();
        }
        if (hasTemplateContent(banners[0])) {
          const err = new Error(
            "Banner has predefined content but component is not in predefined mode",
          );
          logError(err);
          return getErrorElement(err);
        }
        return getBannerElement(
          banners[0],
          this.width,
          this.height,
          this.newTab,
          this.linkContext(),
        );
      },
      error: (error) => getErrorElement(error),
    });
  }

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    super.updated(changedProperties);

    // Always track task status transitions — used by both predefined and standard modes
    const prevStatus = this._prevTaskStatus;
    const currStatus = this.task.status;
    this._prevTaskStatus = currStatus;
    const banners = this.task.value ?? [];

    if (
      this.predefined &&
      prevStatus !== TaskStatus.COMPLETE &&
      currStatus === TaskStatus.COMPLETE
    ) {
      if (banners.length && banners[0].asset?.[0]?.content) {
        try {
          this.applyBannerTemplate(banners[0]);
        } catch (e) {
          logError(e);
        }
      } else if (banners.length) {
        console.warn(
          `[topsort-banner] Predefined mode is set but the auction response for slot "${this.slotId}" contains no content map. Template was not mutated.`,
        );
      }
    }

    // context-mode banner is a container; event emission is the caller's responsibility
    if (!this.isContext) {
      if (prevStatus !== TaskStatus.COMPLETE && currStatus === TaskStatus.COMPLETE) {
        this.emitEvent(banners.length ? "ready" : "nowinners");
      } else if (prevStatus !== TaskStatus.ERROR && currStatus === TaskStatus.ERROR) {
        this.emitEvent("error");
      }
    }

    if (this.isContext && !changedProperties.has("slots")) {
      Promise.resolve().then(() => {
        this.slots = this.renderRoot.querySelectorAll("topsort-banner-slot");
      });
    }

    if (changedProperties.has("width")) {
      this.style.setProperty("--ts-banner-width", this.width > 0 ? `${this.width}px` : "100%");
    }
    if (changedProperties.has("height")) {
      this.style.setProperty("--ts-banner-height", this.height > 0 ? `${this.height}px` : "auto");
    }

    if (
      changedProperties.has("width") ||
      changedProperties.has("height") ||
      changedProperties.has("newTab") ||
      changedProperties.has("language") ||
      changedProperties.has("location")
    ) {
      Promise.resolve().then(() => {
        this.context = {
          ...this.context,
          width: this.width,
          height: this.height,
          newTab: this.newTab,
          language: this.language,
          location: this.location,
          slotId: this.slotId,
        };
      });
    }

    // Runtime language change in predefined mode: re-apply template without
    // re-running the auction. Skip the initial transition (handled above).
    if (
      this.predefined &&
      changedProperties.has("language") &&
      prevStatus === TaskStatus.COMPLETE &&
      currStatus === TaskStatus.COMPLETE &&
      banners.length &&
      banners[0].asset?.[0]?.content
    ) {
      try {
        this.applyBannerTemplate(banners[0]);
      } catch (e) {
        logError(e);
      }
    }
  }

  /** Everything the link hooks need to know about *where* this banner is rendering. */
  private linkContext(): LinkContext {
    return {
      location: this.location,
      slotId: this.slotId,
      language: this.language,
    };
  }

  private applyBannerTemplate(banner: Banner) {
    applyTemplate(this, banner, this.language, (href) =>
      resolveLink(href, banner, this.linkContext()),
    );
  }

  // avoid shadow dom since we cannot attach to events via analytics.js
  protected createRenderRoot() {
    return this;
  }
}

@customElement("topsort-banner-slot")
export class TopsortBannerSlot extends LitElement {
  @consume({ context: bannerContext, subscribe: true })
  @property({ attribute: false })
  readonly context?: BannerContext;

  @property({ attribute: "rank", type: Number })
  readonly rank = 1;

  @property({ type: Boolean })
  readonly predefined: boolean = false;

  private _bannerForRank(): Banner | undefined {
    if (
      !this.context?.banners?.length ||
      this.rank < 1 ||
      this.context.banners.length < this.rank
    ) {
      return undefined;
    }
    return this.context.banners[this.rank - 1];
  }

  protected render() {
    if (this.predefined) {
      return nothing;
    }
    if (!this.context) {
      return html``;
    }
    if (!this.context.banners) {
      return getLoadingElement();
    }
    if (this.context.error) {
      return getErrorElement(this.context.error);
    }
    if (!this.context.banners.length || this.context.banners.length < this.rank) {
      return getNoWinnersElement();
    }
    const banner = this.context.banners[this.rank - 1];
    if (hasTemplateContent(banner)) {
      const err = new Error(
        "Banner has predefined content but component is not in predefined mode",
      );
      logError(err);
      return getErrorElement(err);
    }
    return getBannerElement(
      banner,
      this.context.width,
      this.context.height,
      this.context.newTab,
      this.linkContext(),
    );
  }

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    super.updated(changedProperties);

    if (changedProperties.has("context")) {
      const oldContext = changedProperties.get("context") as BannerContext | undefined;
      const bannersJustArrived = !oldContext?.banners && !!this.context?.banners;

      if (bannersJustArrived) {
        if (this.predefined) {
          const banner = this._bannerForRank();
          if (banner?.asset?.[0]?.content) {
            try {
              this.applyBannerTemplate(banner);
            } catch (e) {
              logError(e);
            }
          } else if (banner) {
            console.warn(
              `[topsort-banner-slot] Predefined mode is set but the auction response for rank ${this.rank} contains no content map. Template was not mutated.`,
            );
          }
          // No banner (no-winners): predefined content stays as-is, no warning needed
        }

        const banner = this._bannerForRank();
        this._emitStateChange(banner ? "ready" : "nowinners");
      } else if (this.predefined && !!this.context?.banners) {
        // Language changed after banners already arrived: re-apply template
        // using the new language, without re-emitting state or re-running auction.
        const languageChanged = oldContext?.language !== this.context?.language;
        if (languageChanged) {
          const banner = this._bannerForRank();
          if (banner?.asset?.[0]?.content) {
            try {
              this.applyBannerTemplate(banner);
            } catch (e) {
              logError(e);
            }
          }
        }
      }

      if (!oldContext?.error && this.context?.error) {
        this._emitStateChange("error");
      }
    }
  }

  /** Slots inherit location/slotId from the context-mode parent's auction. */
  private linkContext(): LinkContext {
    return {
      location: this.context?.location,
      slotId: this.context?.slotId,
      language: this.context?.language,
    };
  }

  private applyBannerTemplate(banner: Banner) {
    applyTemplate(this, banner, this.context?.language, (href) =>
      resolveLink(href, banner, this.linkContext()),
    );
  }

  private _emitStateChange(status: string) {
    this.dispatchEvent(
      new CustomEvent("statechange", {
        detail: { rank: this.rank, status },
        bubbles: true,
        composed: true,
      }),
    );
  }

  // avoid shadow dom since we cannot attach to events via analytics.js
  protected createRenderRoot() {
    return this;
  }
}

@customElement("hls-video")
export class HlsVideo extends LitElement {
  @property({ type: String }) src = ""; // HLS manifest URL
  @property({ type: String }) width = "800px";
  @property({ type: String }) height = "400px";

  private get videoId() {
    try {
      return new URL(this.src).pathname.split("/")[1]; // safer and clearer
    } catch {
      return "hls-video";
    }
  }

  render() {
    return html`
      <video
        id="${this.videoId}"
        style="width:${this.width}; height:${this.height}; object-fit:cover;"
        muted
        autoplay
        loop
        playsinline
      ></video>
    `;
  }

  async firstUpdated() {
    const video = this.shadowRoot?.getElementById(this.videoId) as HTMLVideoElement;
    if (!video) return;

    let Hls: HlsConstructor;
    try {
      Hls = await loadHls();
    } catch (err) {
      console.error("Failed to load HLS.js:", err);
      return;
    }

    if (!Hls) {
      console.error("HLS.js not available after load");
      return;
    }

    const hls = new Hls();
    hls.loadSource(this.src);
    hls.attachMedia(video);
    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      video.play().catch((err) => console.warn("Autoplay failed:", err));
    });
  }
}

let _hlsLoadPromise: Promise<HlsConstructor> | null = null;

function loadHls(): Promise<HlsConstructor> {
  if (_hlsLoadPromise) return _hlsLoadPromise;
  if (window.Hls) {
    _hlsLoadPromise = Promise.resolve(window.Hls);
    return _hlsLoadPromise;
  }
  _hlsLoadPromise = new Promise<HlsConstructor>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://cdn.jsdelivr.net/npm/hls.js@1.6.13/dist/hls.min.js";
    script.onload = () =>
      window.Hls ? resolve(window.Hls) : reject(new Error("HLS.js loaded but not available"));
    script.onerror = () => reject(new Error("Failed to load HLS.js"));
    document.head.appendChild(script);
  }).catch((err) => {
    _hlsLoadPromise = null;
    throw err;
  });
  return _hlsLoadPromise;
}
